import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './motion.css';
import { ReactComponent as SettingIcon } from '@repo/common/assets/icons/20px/icon_20px_setting_outline.svg';
import { ReactComponent as CloseIcon } from '@repo/common/assets/icons/20px/icon_20px_cancel.svg';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import { ReactComponent as InboxIcon } from './icons/inbox.svg';
import { ReactComponent as ChatIcon } from './icons/chat.svg';
import { ReactComponent as SmilePlusIcon } from './icons/smile-plus.svg';
import { cn, initials, isAssigneeOf, roleLabel, visibleTasks } from './model/derive';
import { agoLabel } from './model/inbox';
import { formatDue, tzShortName } from './model/time';
import { useWorkboardCtx } from './model/useWorkboard';
import { loadSound, saveSound } from './ops/inboxAlarm';
import { mentionIdsIn, mentionable } from './CommentExtras';
import { CountBadge } from './primitives';
import TdButton from './Button';
import useMedia from './useMedia';
import usePresence from './usePresence';
import useScrollLock from './useScrollLock';

/** Inbox 패널 (W8) — Needs action · Activity 두 탭, 줄 안 체크인 · 답글 폼, 멘션 · 답글 줄 hover 툴바 */

const QUICK = ['👍', '✅', '❤️', '🎉', '👀'];
const TAB_ORDER = ['action', 'activity'];
const fieldClass =
  'h-9 min-w-0 rounded-full border border-green-deep03/20 bg-grayscale-gray00 px-3.5 text-[13px] text-text-primary outline-none placeholder:text-text-placeholder focus:border-green-deep02';
const cancelClass =
  'h-8 rounded-full px-3 text-[13px] font-medium text-text-secondary hover:text-text-primary';

const Avatar = ({ item }) => {
  const { memberById } = useWorkboardCtx();
  if (!item.actorId) {
    return (
      <span
        aria-hidden
        className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-green-deep02 text-[10px] font-medium text-grayscale-gray00'
      >
        2d
      </span>
    );
  }
  const m = memberById.get(item.actorId);
  const name = m?.name ?? 'Team lead Kim';
  return (
    <span
      title={`${name} · ${roleLabel(m) || 'Team lead'}`}
      className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-surface-green10 text-[10px] font-medium text-green-deep04'
    >
      {initials(name)}
    </span>
  );
};

const Switch = ({ on, onToggle }) => (
  <button
    type='button'
    role='switch'
    aria-checked={on}
    aria-label='Notification sound'
    onClick={onToggle}
    className={cn(
      'relative h-6 w-10 shrink-0 rounded-full transition-colors duration-200',
      on ? 'bg-green-deep02' : 'bg-grayscale-gray04',
    )}
  >
    <span
      className={cn(
        'absolute top-0.5 h-5 w-5 rounded-full bg-grayscale-gray00 shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-[left] duration-200',
        on ? 'left-[18px]' : 'left-0.5',
      )}
    />
  </button>
);

const SoundMenu = ({ open, onToggle, onClose, size }) => {
  const ref = useRef(null);
  const [sound, setSound] = useState(loadSound);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => !ref.current?.contains(e.target) && onClose();
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, onClose]);
  const toggle = () => {
    const next = { ...loadSound(), on: !sound.on };
    saveSound(next);
    setSound(next);
  };
  return (
    <div ref={ref} className='relative'>
      <button
        type='button'
        aria-label='Inbox settings'
        title='Inbox settings'
        aria-expanded={open}
        onClick={onToggle}
        className={cn(
          'flex items-center justify-center rounded-[10px] hover:bg-green-deep03/[0.08]',
          size,
          open && 'bg-green-deep03/[0.08]',
        )}
      >
        <SettingIcon className='h-5 w-5 text-icon-secondary' />
      </button>
      {open && (
        <div
          role='dialog'
          aria-label='Inbox settings'
          className='td-pop absolute right-0 top-[calc(100%+6px)] z-10 flex w-[260px] origin-top-right items-center justify-between gap-3 rounded-2xl border border-green-deep03/[0.08] bg-grayscale-gray00 px-4 py-3.5 shadow-popup-01'
        >
          <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
            Notification sound
          </span>
          <Switch on={sound.on} onToggle={toggle} />
        </div>
      )}
    </div>
  );
};

const CheckInRowForm = ({ item, onDone }) => {
  const ctx = useWorkboardCtx();
  const { data, dispatch, now, displayTz } = ctx;
  const [note, setNote] = useState('');
  const tz = tzShortName(displayTz, now);
  const cp = (data.checkpoints ?? []).find((c) => c.id === item.checkpointId);
  const tasks = visibleTasks(data.tasks).filter(
    (t) =>
      isAssigneeOf(t, data.viewerId) &&
      (!t.acceptedById || t.acceptedById === data.viewerId) &&
      ['in_progress', 'rework_requested'].includes(t.state),
  );
  const ok = !!note.trim() && !ctx.actionsLocked;
  const submit = () => {
    if (!ok || !cp) return;
    dispatch({
      type: 'checkIn',
      checkpointId: cp.id,
      note: note.trim(),
      expected: [],
      at: now.toISOString(),
    });
    const leader = data.members.find((m) => m.role === 'leader')?.name;
    const notified = `${leader ? `${leader} (leader)` : 'Your leader'} gets notified`;
    ctx.toast?.(
      now > new Date(cp.scheduledAt)
        ? { title: 'Checked in late', sub: `${cp.name} · ${notified}` }
        : { title: 'Checked in · On time', sub: notified },
    );
    onDone();
  };
  return (
    <div className='flex flex-col gap-2.5 px-4 pb-3.5 pl-[50px]'>
      <label className='flex flex-col gap-1.5'>
        <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>
          Progress<span className='text-functional-accent03'> *</span>
        </span>
        <input
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && submit()}
          placeholder='What did you finish, and what’s next?'
          className={fieldClass}
        />
      </label>
      {tasks.map((t) => (
        <div key={t.id} className='flex flex-col gap-1.5'>
          <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>
            Expected submit · {t.title}
          </span>
          <span
            title='Change it on Checkpoints'
            className={cn(fieldClass, 'flex items-center tabular-nums')}
          >
            {t.expectedSubmitAt ? `${formatDue(t.expectedSubmitAt, displayTz)} ${tz}` : 'Not set'}
          </span>
        </div>
      ))}
      <div className='flex justify-end gap-1.5'>
        <button type='button' onClick={onDone} className={cancelClass}>
          Cancel
        </button>
        <TdButton size='s' disabled={!ok} onClick={submit}>
          Submit
        </TdButton>
      </div>
    </div>
  );
};

const ReplyRowForm = ({ item, onDone, onRead }) => {
  const ctx = useWorkboardCtx();
  const { data, dispatch, now, memberById } = ctx;
  const [text, setText] = useState('');
  const ok = !!text.trim() && !ctx.actionsLocked;
  const root = (data.comments ?? []).find((c) => c.id === item.replyTo);
  const sent = useRef(false);
  const send = () => {
    // 한 번만 — 빠르게 두 번 눌러도 답글이 둘 생기지 않게
    if (!ok || !root || sent.current) return;
    sent.current = true;
    dispatch({
      type: 'addComment',
      comment: {
        id: `c-${Date.now()}`,
        taskId: root.taskId,
        authorId: data.viewerId,
        type: 'reply',
        body: text.trim(),
        createdAt: now.toISOString(),
        parentId: root.id,
        mentionIds: mentionIdsIn(text, mentionable(memberById, data.viewerId, now)),
      },
    });
    onRead(item);
    ctx.toast?.({ title: 'Reply sent', sub: ctx.taskById.get(root.taskId)?.title });
    onDone();
  };
  return (
    <div className='flex flex-col gap-2 px-4 pb-3.5 pl-[50px]'>
      {item.snippet && (
        <div className='whitespace-pre-line rounded-[10px] bg-green-deep03/[0.04] px-3 py-2.5 text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
          {item.snippet}
        </div>
      )}
      <input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        // 한글 입력 중 Enter 는 글자 확정이라 보내지 않는다 (두 번 달리던 원인)
        onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && send()}
        placeholder='Reply…'
        aria-label='Reply'
        className={fieldClass}
      />
      <div className='flex justify-end gap-1.5'>
        <button type='button' onClick={onDone} className={cancelClass}>
          Cancel
        </button>
        <TdButton size='s' disabled={!ok} onClick={send}>
          Send
        </TdButton>
      </div>
    </div>
  );
};

const Row = ({ item, isAction, expanded, picking, touch, canAct, hrefOf, onOpen, onExpand, onCollapse, onPick, onRead, motion, delay }) => {
  const { data, dispatch, now, displayTz, toast } = useWorkboardCtx();
  const form = usePresence(expanded, 280);
  const [wasDone, setWasDone] = useState(item.done);
  const [justDone, setJustDone] = useState(false);
  if (item.done !== wasDone) {
    setWasDone(item.done);
    setJustDone(item.done);
  }
  const time = agoLabel(item.at, now, displayTz);
  const dot = isAction ? !item.done : item.unread;
  const meta = item.done ? `${item.doneLabel} · ${time}` : isAction ? item.meta : time;
  const metaColor = item.done
    ? 'text-text-tertiary'
    : isAction
      ? item.urgent
        ? 'text-functional-warning04'
        : 'text-green-deep04'
      : 'text-text-secondary';
  const showButton = isAction && canAct && !item.done && !!item.btn && !expanded;
  const tools =
    !isAction && canAct && !expanded && ['mention', 'reply'].includes(item.type) && item.reactTo;
  const comment = item.reactTo ? (data.comments ?? []).find((c) => c.id === item.reactTo) : null;
  // 내가 이 댓글에 단 반응 — Inbox 에서도 보여 준다
  const myReactions = Object.entries(comment?.reactions ?? {})
    .filter(([, ids]) => (ids ?? []).includes(data.viewerId))
    .map(([emoji]) => emoji);
  const [justReacted, setJustReacted] = useState(null);
  // Inbox 에서는 반응을 더하기만 한다 — 이미 단 반응을 다시 눌러도 꺼지지 않는다 (끄기는 태스크 댓글에서)
  const react = (emoji) => {
    if (myReactions.includes(emoji)) {
      toast?.({ title: `You already reacted ${emoji}`, sub: 'Remove it from the comment on the task.' });
      onPick(null);
      return;
    }
    dispatch({ type: 'reactComment', commentId: item.reactTo, emoji, at: now.toISOString() });
    setJustReacted(emoji);
    toast?.({ title: `Reacted ${emoji}` });
    onRead(item);
    onPick(null);
  };

  return (
    <div
      style={delay ? { animationDelay: `${delay}ms` } : undefined}
      className={cn(
        'group relative border-b border-green-deep03/[0.06] transition-colors duration-300',
        expanded && 'bg-green-deep03/[0.03]',
        motion,
      )}
    >
      <a
        href={hrefOf(item)}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onOpen(item);
        }}
        aria-label={`${item.text} · ${meta}`}
        className='grid grid-cols-[24px_minmax(0,1fr)_6px] items-start gap-2.5 px-4 py-3 text-inherit no-underline outline-none transition-colors hover:bg-green-deep03/[0.04] focus-visible:shadow-[inset_0_0_0_2px_#1dc37b] active:bg-green-deep03/[0.08]'
      >
        <Avatar item={item} />
        <span className='flex min-w-0 flex-col gap-1'>
          <span
            className={cn(
              'text-[13px] leading-[150%] transition-colors duration-300 [text-wrap:pretty]',
              dot ? 'font-medium' : 'font-normal',
              item.done ? 'text-text-tertiary' : 'text-text-primary',
            )}
          >
            {item.text}
          </span>
          {/* 댓글 · 반려 사유 · 바뀐 시각 — 두 줄까지 (펼쳐 답글을 쓸 때는 아래 칸에 전부) */}
          {item.snippet && !expanded && (
            <span className='line-clamp-2 whitespace-pre-line text-[12px] leading-[150%] text-text-secondary [overflow-wrap:anywhere]'>
              {item.snippet}
            </span>
          )}
          {myReactions.length > 0 && (
            <span className='flex flex-wrap items-center gap-1'>
              {myReactions.map((emoji) => (
                <span
                  key={emoji}
                  className={cn(
                    'inline-flex h-5 items-center gap-1 rounded-full bg-surface-green10 px-1.5 text-[11px] font-medium text-green-deep04 shadow-[inset_0_0_0_1px_#1dc37b]',
                    emoji === justReacted && 'td-pop',
                  )}
                >
                  <span aria-hidden>{emoji}</span>
                  You reacted
                </span>
              ))}
            </span>
          )}
          <span className='flex flex-wrap items-center gap-1.5'>
            {item.missed && !item.done && (
              <span className='inline-flex h-5 items-center gap-[5px] rounded-full bg-functional-failed01 px-[7px] text-[11px] font-medium text-functional-failed04 shadow-[inset_0_0_0_1px_#ff7474]'>
                <span className='h-[5px] w-[5px] rounded-full bg-functional-failed03' />
                Missed
              </span>
            )}
            {/* 수락 기한이 지난 배정 — 시각 없이 Delayed 칩만 */}
            {item.delayed && !item.done ? (
              <span className='inline-flex h-5 items-center gap-[5px] rounded-full bg-functional-failed01 px-[7px] text-[11px] font-medium text-functional-failed04 shadow-[inset_0_0_0_1px_#ff7474]'>
                <span className='h-[5px] w-[5px] rounded-full bg-functional-failed03' />
                Delayed
              </span>
            ) : (
              <span
                key={String(item.done)}
                className={cn(
                  'text-[12px] font-normal leading-[140%] tabular-nums',
                  metaColor,
                  justDone && 'td-fade',
                )}
              >
                {meta}
              </span>
            )}
          </span>
        </span>
        <span
          title={dot ? (isAction ? 'Not done' : 'Unread') : undefined}
          className={cn(
            'mt-[7px] h-1.5 w-1.5 rounded-full bg-functional-failed03 transition-[opacity,transform] duration-300 motion-reduce:transition-none',
            dot ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
          )}
        />
      </a>

      {showButton && (
        <div className='-mt-1 flex px-4 pb-3 pl-[50px]'>
          <button
            type='button'
            onClick={() => onExpand(item)}
            className='inline-flex h-[26px] items-center gap-0.5 text-[12px] font-medium text-text-primary hover:text-green-deep04 active:opacity-70'
          >
            {item.btn}
            <ChevronIcon aria-hidden className='h-3.5 w-3.5' />
          </button>
        </div>
      )}

      {tools && (
        <div
          className={cn(
            'absolute right-3 top-2 z-[1] flex items-center gap-0.5 rounded-full border border-green-deep03/[0.08] bg-grayscale-gray00 p-[3px] shadow-4 transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
            touch || picking
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none translate-y-0.5 opacity-0 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100',
          )}
        >
          <button
            type='button'
            onClick={() => onExpand(item)}
            className='h-[26px] rounded-full px-2.5 text-[12px] font-medium text-text-primary hover:bg-green-deep03/[0.08]'
          >
            Reply
          </button>
          <button
            type='button'
            aria-label='Add reaction'
            title='Add reaction'
            aria-expanded={picking}
            onClick={() => onPick(picking ? null : item.id)}
            className='flex h-[26px] w-[26px] items-center justify-center rounded-full hover:bg-green-deep03/[0.08]'
          >
            <SmilePlusIcon aria-hidden className='h-4 w-4 text-icon-secondary' />
          </button>
          {picking &&
            QUICK.map((emoji, i) => {
              const on = (comment?.reactions?.[emoji] ?? []).includes(data.viewerId);
              return (
                <button
                  key={emoji}
                  type='button'
                  aria-pressed={on}
                  aria-label={`React ${emoji}`}
                  onClick={() => react(emoji)}
                  style={{ animationDelay: `${i * 30}ms` }}
                  className={cn(
                    'td-pop flex h-[26px] w-[26px] items-center justify-center rounded-full text-[14px] transition-colors duration-150',
                    on ? 'bg-surface-green10' : 'hover:bg-green-deep03/[0.08]',
                  )}
                >
                  {emoji}
                </button>
              );
            })}
        </div>
      )}

      {form.mounted && (item.form === 'checkin' || item.replyTo) && (
        <div className='td-collapse' data-open={form.shown}>
          <div>
            {item.form === 'checkin' ? (
              <CheckInRowForm item={item} onDone={onCollapse} />
            ) : (
              <ReplyRowForm item={item} onDone={onCollapse} onRead={onRead} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const InboxPanel = ({ inbox, mode, shown = true, tab, onTab, exp, onExp, onClose, onOpen, onRead, hrefOf }) => {
  const ctx = useWorkboardCtx();
  const touch = useMedia('(hover: none)');
  const sheet = mode === 'sheet';
  useScrollLock(sheet);
  const [filter, setFilter] = useState('all');
  const [gear, setGear] = useState(false);
  const [pick, setPick] = useState(null);
  const allIds = useMemo(
    () => [...inbox.actions, ...inbox.activity].map((i) => i.id),
    [inbox],
  );
  const [seen, setSeen] = useState(() => new Set(allIds));
  const [arrived, setArrived] = useState(() => new Set());
  const [intro, setIntro] = useState(true);
  useEffect(() => {
    const t = window.setTimeout(() => setIntro(false), 700);
    return () => window.clearTimeout(t);
  }, []);
  // 목록 스크롤 — 새 항목이 위에 붙었는데 아래를 보고 있으면 "↑ N new" 로 알려 주고,
  // 처음 열었을 때 아래에 더 있으면 "Scroll for more" 로 스크롤을 권한다
  const listRef = useRef(null);
  const [above, setAbove] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const flush = () => {
    const fresh = allIds.filter((id) => !seen.has(id));
    if (!fresh.length) return;
    setArrived(new Set(fresh));
    setSeen(new Set(allIds));
    if ((listRef.current?.scrollTop ?? 0) > 24) setAbove((n) => n + fresh.length);
  };
  // 새 항목은 누르지 않아도 바로 목록에 들어온다
  const idsKey = allIds.join('|');
  useEffect(() => {
    flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);
  useEffect(() => {
    if (!arrived.size) return undefined;
    const t = window.setTimeout(() => setArrived(new Set()), 1500);
    return () => window.clearTimeout(t);
  }, [arrived]);

  useEffect(() => {
    if (exp && !seen.has(exp)) flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exp]);

  const state = useRef({});
  state.current = { gear, exp, pick, onExp, onClose };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const own = document.querySelector('[data-inbox]');
      const modal = [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some(
        (el) => !own?.contains(el),
      );
      if (modal) return;
      const s = state.current;
      if (s.gear) setGear(false);
      else if (s.pick) setPick(null);
      else if (s.exp) s.onExp(null);
      else s.onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const [shownTab, setShownTab] = useState(tab);
  const [slide, setSlide] = useState(null);
  const [shownFilter, setShownFilter] = useState(filter);
  const [refilter, setRefilter] = useState(false);
  if (tab !== shownTab) {
    setShownTab(tab);
    setSlide(TAB_ORDER.indexOf(tab) > TAB_ORDER.indexOf(shownTab) ? 'next' : 'prev');
    setRefilter(false);
  }
  if (filter !== shownFilter) {
    setShownFilter(filter);
    setRefilter(true);
  }
  const tabsRef = useRef(null);
  const [bar, setBar] = useState(null);
  useLayoutEffect(() => {
    const place = () => {
      const el = tabsRef.current?.querySelector('[aria-selected="true"]');
      if (el) setBar({ left: el.offsetLeft, width: el.offsetWidth });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [tab, inbox.pending, inbox.unread]);

  const isAction = tab === 'action';
  const source = isAction ? inbox.actions : inbox.activity;
  const filtered = isAction
    ? source
    : source.filter((i) => (filter === 'mentions' ? i.type === 'mention' : true));
  const rows = filtered;
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) setOverflow(el.scrollHeight > el.clientHeight + 8);
  }, [rows.length, tab, filter, shown]);
  const onScroll = (e) => {
    if (e.currentTarget.scrollTop > 8) setScrolled(true);
    if (e.currentTarget.scrollTop <= 24) setAbove(0);
  };
  const toTop = () => {
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setAbove(0);
  };
  const canAct = !ctx.readOnly && !ctx.actionsLocked;
  const btnSize = sheet ? 'h-11 w-11' : 'h-9 w-9';

  const tabs = [
    ['action', 'Needs action', inbox.pending, `${inbox.pending} to do`],
    ['activity', 'Activity', inbox.unread, `${inbox.unread} unread`],
  ];

  return (
    <div
      data-inbox
      role={sheet ? 'dialog' : 'complementary'}
      aria-modal={sheet ? 'true' : undefined}
      aria-label='Inbox'
      className={cn(
        'bg-grayscale-gray00 text-text-primary duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none',
        !shown && 'pointer-events-none',
        mode === 'push' &&
          cn(
            'sticky top-0 h-screen shrink-0 overflow-hidden transition-[width]',
            shown ? 'w-[380px]' : 'w-0',
          ),
        mode === 'overlay' &&
          cn(
            'fixed left-[220px] top-0 z-[940] h-screen w-[380px] border-r border-green-deep03/[0.08] shadow-popup-01 transition-[transform,opacity] [clip-path:inset(0_-48px_0_0)]',
            shown ? 'translate-x-0 opacity-100' : '-translate-x-3 opacity-0',
          ),
        sheet &&
          cn(
            'fixed inset-0 z-[960] transition-transform',
            shown ? 'translate-y-0' : 'translate-y-full',
          ),
      )}
    >
      <div
        className={cn(
          'flex h-full flex-col',
          mode === 'push' ? 'w-[380px] border-r border-green-deep03/[0.08]' : 'w-full',
        )}
      >
        <div className='flex h-14 shrink-0 items-center gap-1 border-b border-green-deep03/[0.08] pl-5 pr-2'>
          <span className='flex-1 text-[16px] font-medium leading-[150%]'>Inbox</span>
          <SoundMenu
            open={gear}
            onToggle={() => setGear((v) => !v)}
            onClose={() => setGear(false)}
            size={btnSize}
          />
          <button
            type='button'
            aria-label='Close inbox'
            title='Close'
            onClick={onClose}
            className={cn(
              'flex items-center justify-center rounded-[10px] hover:bg-green-deep03/[0.08]',
              btnSize,
            )}
          >
            <CloseIcon className='h-5 w-5 text-icon-secondary' />
          </button>
        </div>

        <div
          ref={tabsRef}
          role='tablist'
          aria-label='Inbox'
          className='relative flex shrink-0 gap-1 border-b border-green-deep03/[0.12] px-2.5'
        >
          {tabs.map(([key, label, count, tip]) => {
            const on = tab === key;
            return (
              <button
                key={key}
                type='button'
                role='tab'
                aria-selected={on}
                onClick={() => {
                  onTab(key);
                  onExp(null);
                  setPick(null);
                }}
                className={cn(
                  'flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap px-2.5 text-[14px] transition-colors duration-200',
                  on ? 'font-medium text-text-primary' : 'text-text-secondary hover:text-text-primary',
                )}
              >
                {label}
                <CountBadge count={count} title={tip} />
              </button>
            );
          })}
          {bar && (
            <span
              aria-hidden
              style={{ left: bar.left, width: bar.width }}
              className='absolute bottom-0 h-[2px] bg-green-deep02 transition-[left,width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none'
            />
          )}
        </div>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          <div
            key={tab}
            className={cn('flex min-h-0 flex-1 flex-col', slide && `td-slide-${slide}`)}
          >
            {!isAction && (
              <div role='tablist' aria-label='Filter' className='flex shrink-0 items-center gap-1.5 px-4 pb-1 pt-3'>
                {[
                  ['all', 'All', null],
                  ['mentions', 'Mentions', ChatIcon],
                ].map(([key, label, Icon]) => {
                  const on = filter === key;
                  const iconOnly = !on && Icon;
                  return (
                    <span key={key} className='group relative flex shrink-0'>
                      <button
                        type='button'
                        role='tab'
                        aria-selected={on}
                        aria-label={iconOnly ? label : undefined}
                        onClick={() => setFilter(key)}
                        className={cn(
                          'flex h-9 items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-medium transition-colors duration-200',
                          iconOnly ? 'w-9' : 'px-3',
                          on
                            ? 'bg-grayscale-gray09/[0.07] text-text-primary'
                            : 'text-text-secondary hover:bg-grayscale-gray09/[0.07] hover:text-text-primary',
                        )}
                      >
                        {Icon && (
                          <Icon
                            aria-hidden
                            className={cn('h-[18px] w-[18px] shrink-0', on ? 'text-grayscale-gray08' : 'text-grayscale-gray05')}
                          />
                        )}
                        {!iconOnly && label}
                      </button>
                      {iconOnly && (
                        <span
                          aria-hidden
                          className='pointer-events-none absolute left-1/2 top-[calc(100%+6px)] z-[5] hidden h-7 -translate-x-1/2 items-center whitespace-nowrap rounded-lg bg-grayscale-gray09 px-2.5 text-[12px] font-medium text-grayscale-gray00 group-focus-within:flex group-hover:flex'
                        >
                          {label}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            <div
              key={filter}
              ref={listRef}
              onScroll={onScroll}
              className={cn('relative min-h-0 flex-1 overflow-y-auto', refilter && 'td-fade')}
            >
              {above > 0 && (
                <div className='sticky top-0 z-[2] flex h-0 justify-center'>
                  <button
                    type='button'
                    onClick={toTop}
                    className='td-pop mt-2 h-8 whitespace-nowrap rounded-full bg-[rgba(29,195,123,0.72)] px-3.5 text-[13px] font-medium text-grayscale-gray00 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.4),inset_0_1px_0_rgba(255,255,255,0.45),0_4px_16px_0_rgba(23,20,16,0.08),0_16px_40px_0_rgba(23,20,16,0.08)] backdrop-blur-[12px] backdrop-saturate-[1.8] hover:bg-[rgba(29,195,123,0.88)] active:opacity-[0.85]'
                  >
                    ↑ {above} new
                  </button>
                </div>
              )}
              {rows.map((item, i) => (
                <Row
                  key={item.id}
                  motion={arrived.has(item.id) ? 'td-arrive' : intro ? 'td-rise' : null}
                  delay={intro && !arrived.has(item.id) ? Math.min(i, 8) * 30 : 0}
                  item={item}
                  isAction={isAction}
                  expanded={exp === item.id}
                  picking={pick === item.id}
                  touch={touch}
                  canAct={canAct}
                  hrefOf={hrefOf}
                  onOpen={onOpen}
                  onExpand={(i) => {
                    setPick(null);
                    onExp(i.id);
                  }}
                  onCollapse={() => onExp(null)}
                  onPick={setPick}
                  onRead={onRead}
                />
              ))}
              {/* 아래에 더 있으면 — 스크롤해 보라는 안내 (한 번 스크롤하면 사라진다) */}
              {overflow && !scrolled && (
                <div className='pointer-events-none sticky bottom-0 z-[2] flex h-16 items-end justify-center bg-gradient-to-t from-grayscale-gray00 via-grayscale-gray00/80 to-transparent pb-3'>
                  <button
                    type='button'
                    onClick={() =>
                      listRef.current?.scrollBy({ top: listRef.current.clientHeight * 0.7, behavior: 'smooth' })
                    }
                    className='td-pop pointer-events-auto inline-flex h-7 items-center gap-1 rounded-full bg-grayscale-gray09 px-3 text-[12px] font-medium text-grayscale-gray00 shadow-4'
                  >
                    Scroll for more
                    <span aria-hidden className='td-nudge inline-block'>
                      ↓
                    </span>
                  </button>
                </div>
              )}
              {rows.length === 0 && (
                <div className='td-rise px-4 py-6'>
                  <div className='flex flex-col items-center gap-3 rounded-xl border border-dashed border-green-deep03/[0.24] px-4 py-8 text-center'>
                    <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-surface-green10'>
                      <InboxIcon aria-hidden className='h-5 w-5 text-green-deep02' />
                    </span>
                    <span className='text-[16px] font-medium leading-[140%]'>
                      {isAction ? 'You’re all caught up.' : 'No new activity yet.'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboxPanel;
